// Libs
import React, { useState, useCallback } from "react";
import {
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

// Local
import BaseButtonSecondary from "../BaseButtonSecondary";
import { EMAIL_CAMPAIGN_LIMITS } from "../../constants/email";

/**
 * CSV customer record after parsing
 */
export interface CSVCustomerRecord {
  email: string;
  customer_name?: string;
  customerID?: string;
  rowNumber: number; // For error reporting
}

/**
 * Props for the CSVCustomerUploader component
 */
interface CSVCustomerUploaderProps {
  onCSVData: (data: CSVCustomerRecord[]) => void;
  selectedCount: number;
}

/**
 * Parse CSV content into customer records
 */
function parseCSV(csvContent: string): {
  records: CSVCustomerRecord[];
  errors: string[];
} {
  const lines = csvContent.trim().split("\n");
  const records: CSVCustomerRecord[] = [];
  const errors: string[] = [];

  if (lines.length === 0) {
    errors.push("CSV file is empty");
    return { records, errors };
  }

  // Parse header row
  const headerLine = lines[0].trim();
  const headers = headerLine.split(",").map((h) => h.trim().toLowerCase());

  // Validate required headers
  const emailIndex = headers.findIndex((h) =>
    ["email", "email_address", "emailaddress"].includes(h),
  );
  if (emailIndex === -1) {
    errors.push(
      "CSV must contain an 'email' column (or 'email_address', 'emailaddress')",
    );
    return { records, errors };
  }

  const nameIndex = headers.findIndex((h) =>
    ["name", "customer_name", "customername", "full_name", "fullname"].includes(
      h,
    ),
  );

  const customerIdIndex = headers.findIndex((h) =>
    ["customer_id", "customerid", "customer_id", "id"].includes(h),
  );

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    const columns = line.split(",").map((c) => c.trim().replace(/^"|"$/g, "")); // Remove quotes
    const rowNumber = i + 1;

    const email = columns[emailIndex]?.trim();
    if (!email) {
      errors.push(`Row ${rowNumber}: Email is required`);
      continue;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errors.push(`Row ${rowNumber}: Invalid email format: ${email}`);
      continue;
    }

    const customer_name =
      nameIndex >= 0 ? columns[nameIndex]?.trim() : undefined;

    const customerID =
      customerIdIndex >= 0 ? columns[customerIdIndex]?.trim() : undefined;

    records.push({
      email,
      customer_name: customer_name || undefined,
      customerID: customerID || undefined,
      rowNumber,
    });
  }

  return { records, errors };
}

/**
 * CSV Customer Uploader Component
 *
 * Allows users to upload a CSV file containing email addresses and optional customer names
 * for email campaigns. Validates the CSV format and provides preview of the data.
 */
export function CSVCustomerUploader({
  onCSVData,
  selectedCount,
}: CSVCustomerUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCSVData] = useState<CSVCustomerRecord[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (!selectedFile) return;

      // Validate file type
      if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
        setErrors(["Please select a CSV file"]);
        setFile(null);
        setCSVData([]);
        return;
      }

      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setErrors(["File size must be under 10MB"]);
        setFile(null);
        setCSVData([]);
        return;
      }

      setFile(selectedFile);
      setErrors([]);
      processFile(selectedFile);
    },
    [],
  );

  const processFile = useCallback(
    (file: File) => {
      setIsProcessing(true);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvContent = e.target?.result as string;
          const { records, errors } = parseCSV(csvContent);

          setCSVData(records);
          setErrors(errors);

          if (errors.length === 0 && records.length > 0) {
            onCSVData(records);
          }
        } catch (error) {
          setErrors(["Failed to parse CSV file"]);
          setCSVData([]);
        } finally {
          setIsProcessing(false);
        }
      };

      reader.onerror = () => {
        setErrors(["Failed to read file"]);
        setIsProcessing(false);
      };

      reader.readAsText(file);
    },
    [onCSVData],
  );

  const handleClear = useCallback(() => {
    setFile(null);
    setCSVData([]);
    setErrors([]);
    setShowPreview(false);
    onCSVData([]);
  }, [onCSVData]);

  const remainingEmails = EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS - selectedCount;
  const isNearLimit = remainingEmails < 100;
  const isOverLimit = selectedCount > EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS;

  // Sample CSV format for download
  const sampleCSV = `email,customer_name,customerID
john@example.com,John Smith,CUST-001
jane@example.com,Jane Doe,CUST-002
bob@company.com,Bob Johnson,CUST-003`;

  const handleDownloadSample = () => {
    const blob = new Blob([sampleCSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_customers.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Email Limit Warning */}
      <div
        className={`rounded-lg border p-4 ${
          isOverLimit
            ? "border-red-200 bg-red-50"
            : isNearLimit
              ? "border-yellow-200 bg-yellow-50"
              : "border-blue-200 bg-blue-50"
        }`}
      >
        <div className="flex items-center gap-2">
          {(isOverLimit || isNearLimit) && (
            <ExclamationTriangleIcon
              className={`h-5 w-5 ${
                isOverLimit ? "text-red-600" : "text-yellow-600"
              }`}
            />
          )}
          <div>
            <div className="text-sm font-medium">
              Email Campaign Limit:{" "}
              {EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS.toLocaleString()} emails
            </div>
            <div
              className={`text-sm ${
                isOverLimit
                  ? "text-red-600"
                  : isNearLimit
                    ? "text-yellow-600"
                    : "text-gray-600"
              }`}
            >
              {isOverLimit
                ? `Over limit by ${(selectedCount - EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS).toLocaleString()} emails. Please remove some recipients.`
                : `${selectedCount.toLocaleString()} selected • ${remainingEmails.toLocaleString()} remaining`}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900">Upload CSV File</h3>
          <p className="mt-1 text-sm text-gray-600">
            Upload a CSV file containing email addresses and optional customer
            names.
          </p>
        </div>

        {/* Sample CSV Download */}
        <div className="mb-4 rounded-md bg-gray-50 p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700">
                CSV Format Requirements:
              </div>
              <div className="text-sm text-gray-600">
                Required:{" "}
                <code className="rounded bg-gray-200 px-1">email</code> column
                <br />
                Optional:{" "}
                <code className="rounded bg-gray-200 px-1">
                  customer_name
                </code>,{" "}
                <code className="rounded bg-gray-200 px-1">customerID</code>{" "}
                columns
              </div>
            </div>
            <BaseButtonSecondary
              onClick={handleDownloadSample}
              className="text-sm"
            >
              Download Sample CSV
            </BaseButtonSecondary>
          </div>
        </div>

        {/* File Upload Area */}
        {!file ? (
          <div className="flex justify-center">
            <label
              htmlFor="csv-upload"
              className="relative cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <span className="mt-2 block text-sm font-medium text-gray-900">
                Upload CSV file
              </span>
              <span className="mt-1 block text-sm text-gray-500">
                or drag and drop
              </span>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            {/* File Info */}
            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center gap-3">
                <DocumentArrowUpIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {file.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
              <button
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Processing indicator */}
            {isProcessing && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                Processing CSV file...
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-600" />
                  <div>
                    <div className="text-sm font-medium text-red-800">
                      CSV Validation Errors:
                    </div>
                    <ul className="mt-1 text-sm text-red-700">
                      {errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Success and preview */}
            {csvData.length > 0 && errors.length === 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircleIcon className="h-5 w-5" />
                  Successfully loaded {csvData.length} email
                  {csvData.length !== 1 ? "s" : ""}
                </div>

                {/* Preview toggle */}
                <div className="flex items-center gap-3">
                  <BaseButtonSecondary
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 text-sm"
                  >
                    <EyeIcon className="h-4 w-4" />
                    {showPreview ? "Hide Preview" : "Show Preview"}
                  </BaseButtonSecondary>
                </div>

                {/* Preview table */}
                {showPreview && (
                  <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                            Email
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                            Customer Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                            Customer ID
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {csvData.slice(0, 10).map((record, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {record.email}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {record.customer_name || "—"}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {record.customerID || "—"}
                            </td>
                          </tr>
                        ))}
                        {csvData.length > 10 && (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-4 py-2 text-center text-sm text-gray-500"
                            >
                              ... and {csvData.length - 10} more
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
